import {
  AdviceData,
  AdviceDocumentType,
  AdviceProduct,
  AdviceProductDetailsBackendValues,
  AdviceProductDetailsFormValues,
  AdvisorValues,
  CartAdviceValues,
  DisabilityAdditionalData,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import isEqual from "lodash.isequal";
import omit from "lodash.omit";
import {
  ADVICE_NAMES,
  Advice,
  AdviceChange,
  AdviceChanges,
  AdvicePriceCalculationRequest,
  AdvicePriceCalculationResult,
  AdviceState,
  InsuranceAdvice,
  PurchaseRecommendation,
  SinglePayment,
  YearlyPayment,
} from "./interfaces";
import {
  OldPriceCalculationRequest,
  getCorrectAdviceValues,
  mapFormToProductPriceRequest,
} from "./productPriceRequestMappers";

import { mapDetailsFormDataToAdviceData } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/utils";
import { OneTimePaymentAdvisorChoice } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/interfaces";
import { insuredSumFromAdviceProduct } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/hooks/useInitialFormValues";
import { mapInsuranceNameToAdviceCoverageName } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { Cart } from "Store/cart/cartInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { isDefined } from "Utils/functions";

export const isAdviced = (insuranceAdvice?: InsuranceAdvice): boolean => {
  if (insuranceAdvice) {
    const recommendation =
      getCorrectPayment(insuranceAdvice).recommendationConfiguration
        .recommendation;
    return (
      recommendation === PurchaseRecommendation.PURCHASE ||
      recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR ||
      recommendation ===
        PurchaseRecommendation.CHECK_DISABILITY_PENSION_AND_PURCHASE ||
      recommendation === PurchaseRecommendation.NOT_RECOMMENDED
    );
  }
  return false;
};

export const isChecked = (insuranceAdvice?: InsuranceAdvice): boolean => {
  if (insuranceAdvice) {
    const recommendation =
      getCorrectPayment(insuranceAdvice).recommendationConfiguration
        .recommendation;
    return (
      recommendation === PurchaseRecommendation.PURCHASE ||
      recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR ||
      recommendation ===
        PurchaseRecommendation.CHECK_DISABILITY_PENSION_AND_PURCHASE
    );
  }
  return false;
};

export const getCorrectPayment = (
  insuranceAdvice: InsuranceAdvice,
): YearlyPayment | SinglePayment =>
  insuranceAdvice.yearlyPayment !== undefined
    ? insuranceAdvice.yearlyPayment
    : insuranceAdvice.singlePayment;

const getAdviceInitialRequest = (initialRequest: AdviceChanges): AdviceData[] =>
  Object.keys(initialRequest).map((insuranceName: ADVICE_NAMES) => {
    const insuranceMonthlyPrice =
      initialRequest[insuranceName]?.insuranceMonthlyPrice;
    return {
      ...mapAdviceChangeToAdviceData(insuranceName, initialRequest),
      ...(insuranceMonthlyPrice && { insuranceMonthlyPrice }),
    };
  });

export const getAdviceChangedByCustomer = (
  changedByCustomer: AdviceChanges,
  advicePriceCalculationResults?: AdvicePriceCalculationResult[],
): AdviceData[] =>
  Object.keys(changedByCustomer).map((insuranceName: ADVICE_NAMES) => {
    const insuranceMonthlyPrice = getInsuranceMonthlyPrice(
      insuranceName,
      advicePriceCalculationResults,
    );
    return {
      ...mapAdviceChangeToAdviceData(insuranceName, changedByCustomer),
      ...(insuranceMonthlyPrice && { insuranceMonthlyPrice }),
    };
  });

const mapAdviceChangeToAdviceData = (
  insuranceName: ADVICE_NAMES,
  adviceChanges: AdviceChanges,
): AdviceData => {
  const coverageAmountCapital: number =
    adviceChanges[insuranceName]?.priceCalculationRequest?.data
      ?.coverageAmountCapital ?? 0;
  const disabilityOneTimePaymentChecked =
    coverageAmountCapital > 0 || undefined;
  return {
    insuranceName,
    productCheckedToPurchase:
      adviceChanges[insuranceName].productCheckedToPurchase,
    sliderValue:
      adviceChanges[insuranceName].priceCalculationRequest?.data?.insuredSum ??
      0,
    additionalData: {
      ...adviceChanges[insuranceName].additionalData,
      ...(disabilityOneTimePaymentChecked
        ? {
            disabilityOneTimePaymentChecked,
          }
        : undefined),
      ...(coverageAmountCapital > 0
        ? {
            disabilityOneTimePaymentValue: coverageAmountCapital,
          }
        : undefined),
    },
  };
};

const getInsuranceMonthlyPrice = (
  insuranceName: ADVICE_NAMES,
  advicePriceCalculationResults?: AdvicePriceCalculationResult[],
): number | undefined => {
  if (!advicePriceCalculationResults) {
    return undefined;
  }
  const priceResult: AdvicePriceCalculationResult | undefined =
    advicePriceCalculationResults.find(
      (price) =>
        price.configurationName ===
        mapInsuranceNameToAdviceCoverageName(insuranceName),
    );
  return priceResult ? priceResult.prices[0].monthlyPricePrecise : undefined;
};

export const formValuesDiff = (
  isAdvisor: boolean,
  adviceProductValues?: AdviceProductDetailsBackendValues,
  adviceFormValues?: AdviceProductDetailsFormValues,
  advisorValues?: AdvisorValues,
): boolean => {
  if (!adviceFormValues) {
    return false;
  }
  const mappedAdviceFormValues = mapDetailsFormDataToAdviceData(
    adviceFormValues,
    isAdvisor,
    advisorValues,
  );
  if (isAdvisor && adviceProductValues) {
    const properties = ["isAdvisor", "advisorValues", "situationComment"];
    const backendValues = omit(adviceProductValues, properties);
    const formValues = omit(mappedAdviceFormValues, properties);
    return !isEqual(backendValues, formValues);
  }
  return !isEqual(adviceProductValues, mappedAdviceFormValues);
};

export const isAdviceProductOnCart = (
  product: InsuranceProduct,
  cart: Cart | null,
): boolean => {
  if (!isAdviceProduct(product.name)) {
    return false;
  }

  return (
    (cart?.advice?.adviceProducts ?? []).find(
      (adviceProduct) =>
        adviceProduct.productId === product.productId &&
        adviceProduct.insuranceName === product.name,
    ) !== undefined
  );
};

export const isAdviceProduct = (productName: INSURANCE_NAME_VALUE): boolean =>
  productName === INSURANCE_NAME_VALUE.DISABILITY_FLEX ||
  productName === INSURANCE_NAME_VALUE.DEATH ||
  productName === INSURANCE_NAME_VALUE.CRITICAL_ILLNESS ||
  productName === INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS ||
  productName === INSURANCE_NAME_VALUE.AKA_GROUP_LIFE;

export const removeOpenForAdvisors = (
  url: string,
  isAdvisor: boolean,
): string => {
  return isAdvisor ? url.replace("/open", "") : url;
};

export const getAdviceUpdatePayloadWithPrices = (
  isAdvisor: boolean,
  adviceProducts: AdviceProduct[],
  advice: AdviceState,
  adviceRequest: AdviceProductDetailsBackendValues,
  advisorValues: AdvisorValues,
  adviceDocumentId?: string,
): CartAdviceValues => {
  const advicePriceCalculationResults =
    advice.advicePriceCalculationResults.current ?? undefined;
  const { initialRequest, changedByCustomer } =
    advice.advicePriceCalculationRequests;
  const adviceUpdatePayload: CartAdviceValues = {
    adviceProductValues: adviceRequest,
    adviceInitialRequest: getAdviceInitialRequest(initialRequest),
    adviceProducts,
  };
  adviceUpdatePayload.adviceChangedByCustomer = getAdviceChangedByCustomer(
    changedByCustomer,
    advicePriceCalculationResults,
  );
  if (isDefined(adviceDocumentId)) {
    adviceUpdatePayload.adviceDocumentId = adviceDocumentId;
    adviceUpdatePayload.adviceDocumentType = isAdvisor
      ? AdviceDocumentType.ADVICE_RECOMMENDED
      : AdviceDocumentType.ADVICE_CHOSEN;
  }
  if (isAdvisor) {
    adviceUpdatePayload.adviceProductValues = {
      ...adviceUpdatePayload.adviceProductValues,
      isAdvisor,
      advisorValues,
    };
  }
  return adviceUpdatePayload;
};

export function createOldPriceCalculationRequest(
  adviceFormValues: AdviceProductDetailsFormValues,
  key:
    | INSURANCE_NAME_VALUE.DISABILITY_FLEX
    | INSURANCE_NAME_VALUE.DEATH
    | INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
  cart: Cart | null,
  payload: Advice,
  advicePriceCalculationRequests: AdvicePriceCalculationRequest,
  isAdvisor: boolean,
): {
  productCheckedToPurchase: boolean | undefined;
  oldPriceCalculationRequest: OldPriceCalculationRequest;
} {
  let oldPriceCalculationRequest = mapFormToProductPriceRequest(
    adviceFormValues,
    key,
  );
  oldPriceCalculationRequest.data.insuredSum = getCorrectAdviceValues(
    getCorrectPayment(payload.advice[key]),
    isAdvisor,
  ).adviceValue;
  const changesDoneByCustomer: AdviceChange | undefined =
    advicePriceCalculationRequests?.changedByCustomer[key];
  if (isDefined(changesDoneByCustomer)) {
    oldPriceCalculationRequest =
      updateOldPriceCalculationRequestWithChangesDoneByCustomer(
        isAdvisor,
        payload.advice[key].singlePayment,
        oldPriceCalculationRequest,
        changesDoneByCustomer,
      );
  }

  if (isAdvisor) {
    const insuredSum = insuredSumFromAdviceProduct(key, cart);
    if (isDefined(insuredSum)) {
      oldPriceCalculationRequest.data.insuredSum = insuredSum;
    }
  }

  return {
    oldPriceCalculationRequest,
    productCheckedToPurchase: changesDoneByCustomer?.productCheckedToPurchase,
  };
}

const updateOldPriceCalculationRequestWithChangesDoneByCustomer = (
  isAdvisor: boolean,
  singlePayment: SinglePayment,
  oldPriceCalculationRequest: OldPriceCalculationRequest,
  changesDoneByCustomer: AdviceChange,
): OldPriceCalculationRequest => {
  oldPriceCalculationRequest.data.insuredSum =
    changesDoneByCustomer.sliderValue;
  const coverageAmountCapital = getCorrectCoverageAmountCapital(
    isAdvisor,
    singlePayment,
    changesDoneByCustomer.additionalData,
  );
  if (coverageAmountCapital > 0) {
    oldPriceCalculationRequest.data.coverageAmountCapital =
      coverageAmountCapital;
  }
  if (changesDoneByCustomer.additionalData?.disabilityPaymentAfter3Months) {
    oldPriceCalculationRequest.data.paymentAfter3Months =
      changesDoneByCustomer.additionalData?.disabilityPaymentAfter3Months;
  }
  return oldPriceCalculationRequest;
};

const getCorrectCoverageAmountCapital = (
  isAdvisor: boolean,
  singlePayment: SinglePayment,
  additionalData?: DisabilityAdditionalData,
): number => {
  if (!isDefined(additionalData)) {
    return 0;
  }
  if (isAdvisor && additionalData.disabilityOneTimePaymentChoice) {
    switch (additionalData.disabilityOneTimePaymentChoice) {
      case OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT:
        return getCorrectAdviceValues(singlePayment, true).adviceValue;
      case OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT:
        return additionalData.disabilityOneTimePaymentAdvisorValue ?? 0;
      case OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT:
      default:
        return 0;
    }
  }
  return additionalData.disabilityOneTimePaymentChecked
    ? getCorrectAdviceValues(singlePayment).adviceValue
    : 0;
};
